import { addDays, addWeeks, addMonths, addYears, isWeekend, getDay } from 'date-fns';
import type { RecurrencePattern } from './recurrenceTypes';

export interface RecurringEventData {
  title: string;
  description?: string | null;
  start_date: string;
  end_date: string;
  event_type: 'family' | 'personal' | 'appointment' | 'birthday' | 'holiday';
  location?: string | null;
  is_recurring: boolean;
  recurrence_pattern?: string | null;
  linked_persons?: string[];
}

export function generateRecurringEvents(
  baseEvent: RecurringEventData,
  pattern: RecurrencePattern,
  maxOccurrences: number = 104 // 2 years for weekly events
): RecurringEventData[] {
  if (pattern === 'none') {
    return [{ ...baseEvent, is_recurring: false, recurrence_pattern: '' }];
  }

  const events: RecurringEventData[] = [];
  const startDate = new Date(baseEvent.start_date);
  const endDate = new Date(baseEvent.end_date);
  const eventDuration = endDate.getTime() - startDate.getTime();

  for (let i = 0; i < maxOccurrences; i++) {
    let nextStartDate: Date;
    
    switch (pattern) {
      case 'daily':
        nextStartDate = addDays(startDate, i);
        break;
      case 'weekdays':
        nextStartDate = addDays(startDate, i);
        // Skip weekends
        if (isWeekend(nextStartDate)) continue;
        break;
      case 'weekly':
        nextStartDate = addWeeks(startDate, i);
        break;
      case 'every-monday':
      case 'every-tuesday':
      case 'every-wednesday':
      case 'every-thursday':
      case 'every-friday':
      case 'every-saturday':
      case 'every-sunday': {
        // Map pattern to day of week (0 = Sunday, 1 = Monday, etc.)
        const dayMap: Record<string, number> = {
          'every-sunday': 0,
          'every-monday': 1,
          'every-tuesday': 2,
          'every-wednesday': 3,
          'every-thursday': 4,
          'every-friday': 5,
          'every-saturday': 6
        };
        const targetDay = dayMap[pattern];
        
        // Start from the base date and add weeks
        nextStartDate = addWeeks(startDate, i);
        
        // Ensure we're on the correct day of week
        const currentDay = getDay(nextStartDate);
        if (i === 0 && currentDay !== targetDay) {
          // If first occurrence is not on the target day, skip to next week
          const daysUntilTarget = (targetDay - currentDay + 7) % 7;
          nextStartDate = addDays(nextStartDate, daysUntilTarget);
        }
        break;
      }
      case 'monthly':
        nextStartDate = addMonths(startDate, i);
        break;
      case 'yearly':
        nextStartDate = addYears(startDate, i);
        break;
      default:
        nextStartDate = startDate;
    }

    const nextEndDate = new Date(nextStartDate.getTime() + eventDuration);

    events.push({
      ...baseEvent,
      start_date: nextStartDate.toISOString(),
      end_date: nextEndDate.toISOString(),
      is_recurring: true,
      recurrence_pattern: pattern
    });

    // Limit generation to reasonable timeframes
    const yearLimit = pattern === 'yearly' ? 10 : pattern === 'monthly' ? 24 : 104;
    if (i >= yearLimit) break;
  }

  return events;
}

export function createAllDayEvent(date: Date): { start: Date; end: Date } {
  // Utiliser UTC pour éviter les décalages de fuseau horaire
  const start = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0, 0, 0, 0
  ));
  
  const end = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23, 59, 59, 999
  ));
  
  return { start, end };
}